import { getAPIURL } from "./config";

/**
 * Список проектов графика + фильтры
 * GET /project_progress/projects
 * @param {{ year?: string|number, building_type?: string, chief_id?: number|string, status?: string }} [params]
 */
export const getProjectProgressProjects = async (params = {}) => {
  const search = new URLSearchParams();
  if (params.year != null && params.year !== "") {
    search.set("year", String(params.year));
  }
  if (params.building_type) {
    search.set("building_type", params.building_type);
  }
  if (params.chief_id != null && params.chief_id !== "") {
    search.set("chief_id", String(params.chief_id));
  }
  if (params.status) {
    search.set("status", params.status);
  }

  const query = search.toString();
  const url = `${getAPIURL()}project_progress/projects${query ? `?${query}` : ""}`;

  const response = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    credentials: "include",
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `Ошибка загрузки графика проектов: ${response.status} ${response.statusText}${
        errorText ? `. ${errorText}` : ""
      }`,
    );
  }

  return response.json();
};

/**
 * Детальный просмотр проекта с разделами и видами работ
 * GET /project_progress/projects/{project_id}
 * @param {number|string} projectId
 * @param {{ include_work_types?: boolean }} [params]
 */
export const getProjectProgressDetail = async (
  projectId,
  params = { include_work_types: true },
) => {
  const search = new URLSearchParams();
  if (params.include_work_types != null) {
    search.set("include_work_types", String(params.include_work_types));
  }

  const query = search.toString();
  const url = `${getAPIURL()}project_progress/projects/${projectId}${
    query ? `?${query}` : ""
  }`;

  const response = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    credentials: "include",
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `Ошибка загрузки графика проекта: ${response.status} ${response.statusText}${
        errorText ? `. ${errorText}` : ""
      }`,
    );
  }

  return response.json();
};

/**
 * Все виды работ проекта по разделам
 * GET /project_progress/projects/{project_id}/work_types
 * @param {number|string} projectId
 */
export const getProjectWorkTypesBySection = async (projectId) => {
  const url = `${getAPIURL()}project_progress/projects/${projectId}/work_types`;

  const response = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    credentials: "include",
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      parseProgressApiError(
        errorText,
        `Ошибка загрузки видов работ проекта: ${response.status} ${response.statusText}`,
      ),
    );
  }

  return response.json();
};

/**
 * Обновление раздела графика проекта
 * PATCH /project_progress/projects/{project_id}/sections/{section_id}
 * @param {number|string} projectId
 * @param {number|string} sectionId
 * @param {{ project_section_id?: number, responsible_user_id?: number, plan_start_date?: string|null, plan_end_date?: string|null, order_index?: number }} body
 */
export const updateProjectProgressSection = async (
  projectId,
  sectionId,
  body,
) => {
  const url = `${getAPIURL()}project_progress/projects/${projectId}/sections/${sectionId}`;

  const response = await fetch(url, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    credentials: "include",
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      parseProgressApiError(
        errorText,
        `Ошибка обновления раздела: ${response.status} ${response.statusText}`,
      ),
    );
  }

  return response.json();
};

/**
 * Виды работ раздела + сумма удельных весов
 * GET /project_progress/projects/{project_id}/sections/{section_id}/work_types
 * @param {number|string} projectId
 * @param {number|string} sectionId
 */
export const getSectionWorkTypes = async (projectId, sectionId) => {
  const url = `${getAPIURL()}project_progress/projects/${projectId}/sections/${sectionId}/work_types`;

  const response = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    credentials: "include",
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `Ошибка загрузки видов работ: ${response.status} ${response.statusText}${
        errorText ? `. ${errorText}` : ""
      }`,
    );
  }

  return response.json();
};

/**
 * Добавление вида работ или вехи в раздел
 * POST /project_progress/projects/{project_id}/sections/{section_id}/work_types
 * body.is_milestone: true — веха, false — вид работ
 */
export const createSectionWorkType = async (projectId, sectionId, body) => {
  const url = `${getAPIURL()}project_progress/projects/${projectId}/sections/${sectionId}/work_types`;

  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    credentials: "include",
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const errorText = await response.text();
    let message = `Ошибка добавления вида работ: ${response.status} ${response.statusText}`;
    try {
      const err = JSON.parse(errorText);
      if (typeof err.detail === "string") {
        message = err.detail;
      } else if (Array.isArray(err.detail)) {
        message = err.detail.map((d) => d.msg || JSON.stringify(d)).join("; ");
      } else if (err.message) {
        message = err.message;
      }
    } catch {
      if (errorText) message += `. ${errorText}`;
    }
    throw new Error(message);
  }

  return response.json();
};

const parseProgressApiError = (errorText, fallback) => {
  let message = fallback;
  try {
    const err = JSON.parse(errorText);
    if (typeof err.detail === "string") {
      message = err.detail;
    } else if (Array.isArray(err.detail)) {
      message = err.detail.map((d) => d.msg || JSON.stringify(d)).join("; ");
    } else if (err.message) {
      message = err.message;
    }
  } catch {
    if (errorText) message += `. ${errorText}`;
  }
  return message;
};

/**
 * Обновление вида работ
 * PATCH /project_progress/projects/{project_id}/work_types/{work_type_id}
 */
export const updateProjectProgressWorkType = async (
  projectId,
  workTypeId,
  body,
) => {
  const url = `${getAPIURL()}project_progress/projects/${projectId}/work_types/${workTypeId}`;

  const response = await fetch(url, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    credentials: "include",
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      parseProgressApiError(
        errorText,
        `Ошибка обновления вида работ: ${response.status} ${response.statusText}`,
      ),
    );
  }

  return response.json();
};

/**
 * Удаление вида работ
 * DELETE /project_progress/projects/{project_id}/work_types/{work_type_id}
 */
export const deleteProjectProgressWorkType = async (projectId, workTypeId) => {
  const url = `${getAPIURL()}project_progress/projects/${projectId}/work_types/${workTypeId}`;

  const response = await fetch(url, {
    method: "DELETE",
    headers: {
      Accept: "application/json",
    },
    credentials: "include",
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      parseProgressApiError(
        errorText,
        `Ошибка удаления вида работ: ${response.status} ${response.statusText}`,
      ),
    );
  }

  if (response.status === 204) return { message: "ok" };
  try {
    return await response.json();
  } catch {
    return { message: "ok" };
  }
};

/**
 * Зафиксировать план раздела
 * POST /project_progress/projects/{project_id}/sections/{section_id}/fix_plan
 */
export const fixSectionPlan = async (projectId, sectionId) => {
  const url = `${getAPIURL()}project_progress/projects/${projectId}/sections/${sectionId}/fix_plan`;

  const response = await fetch(url, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    credentials: "include",
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      parseProgressApiError(
        errorText,
        `Ошибка фиксации плана: ${response.status} ${response.statusText}`,
      ),
    );
  }

  return response.json();
};

/**
 * Отменить фиксацию плана раздела (только инициатор фиксации)
 * POST /project_progress/projects/{project_id}/sections/{section_id}/unfix_plan
 */
export const unfixSectionPlan = async (projectId, sectionId) => {
  const url = `${getAPIURL()}project_progress/projects/${projectId}/sections/${sectionId}/unfix_plan`;

  const response = await fetch(url, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    credentials: "include",
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      parseProgressApiError(
        errorText,
        `Ошибка отмены фиксации плана: ${response.status} ${response.statusText}`,
      ),
    );
  }

  return response.json();
};

/**
 * Известные названия видов работ / вех каталожного раздела (для селекта)
 * GET /project_progress/projects/{project_id}/sections/{section_id}/known_work_type_names
 * @param {boolean} [isMilestone=false] — true: только вехи; false: только обычные виды работ
 */
export const getKnownWorkTypeNames = async (
  projectId,
  sectionId,
  { isMilestone = false } = {},
) => {
  const params = new URLSearchParams({
    is_milestone: String(Boolean(isMilestone)),
  });
  const url = `${getAPIURL()}project_progress/projects/${projectId}/sections/${sectionId}/known_work_type_names?${params}`;

  const response = await fetch(url, {
    method: "GET",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    credentials: "include",
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      parseProgressApiError(
        errorText,
        `Ошибка загрузки списка ${isMilestone ? "вех" : "видов работ"}: ${response.status} ${response.statusText}`,
      ),
    );
  }

  return response.json();
};
